import { FC, useEffect, useState } from 'react';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupTimePicker from 'components/ui/FormGroupTimePicker/FormGroupTimePicker';
import { useTranslation } from 'react-i18next';
import { BookingModel, ForwardPickup } from 'ts/transfer/interfaces/TransferResultsResponse';
import InfoFormAddress from './InfoFormAddress';
import { getFlightInformationValidation } from 'constants/transfer';
import { dateFormat } from 'utils/transfer';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface InfoFormProps {
  bookingModel: BookingModel;
  isSubmitted: boolean;
  type: 'forward' | 'return';
  onChange: (model: BookingModel) => void;
}

type Pickup = 'forwardPickup' | 'returnPickup';
type Dropoff = 'forwardDropoff' | 'returnDropoff';

const InfoForm: FC<InfoFormProps> = ({ bookingModel, isSubmitted, type, ...props }) => {
  const [t] = useTranslation();
  const [typePickup, setTypePickup] = useState<Pickup>('forwardPickup');
  const [typeDropoff, setTypeDropoff] = useState<Dropoff>('forwardDropoff');
  const [model, setModel] = useState(bookingModel);
  const pickupType = model[type].transfer.pickUpDetails.city ? 'city' : 'airport';
  const dropoffType = model[type].transfer.dropOffDetails.city ? 'city' : 'airport';

  const country = model[type].transfer.pickUpDetails.country;

  useEffect(() => {
    if (type === 'return') {
      setTypePickup('returnPickup');
      setTypeDropoff('returnDropoff');
    } else {
      setTypePickup('forwardPickup');
      setTypeDropoff('forwardDropoff');
    }
  }, []); // eslint-disable-line

  const onChangeField = (field: Pickup | Dropoff, value: ForwardPickup) => {
    const newModel = { ...model, [field]: value };

    props.onChange(newModel);
    setModel(newModel);
  };

  return (
    <div className="form-wrapper row">
      <FormGroup
        value={model[type].transfer.pickUpDetails[pickupType]?.desc}
        onChange={() => {}}
        label={model[type].transfer.pickUpDetails.city ? t('hotel.address') : t('flight.airport')}
        className="col-12 col-md-6"
        disabled={true}
      />
      <FormGroup
        value={country?.desc || country?.code || ''}
        onChange={() => {}}
        label={t('package.country')}
        className="col-12 col-md-6"
        disabled={true}
      />
      <InfoFormAddress
        isSubmitted={isSubmitted}
        type={pickupType}
        onChange={(value) => onChangeField(typePickup, value)}
        value={model[typePickup]}
        validation={getFlightInformationValidation(pickupType === 'airport')}
      />

      <FormGroup
        value={dateFormat(model[type].transfer.pickUpDate, 'dd-MM-yyyy')}
        onChange={() => {}}
        label={t('flight.date')}
        className="col-12 col-md-2"
        isSubmitted={isSubmitted}
        disabled={true}
      />
      <FormGroupTimePicker
        value={new Date('2000-11-11T' + model[typePickup].serviceTime + 'Z')}
        onChange={(time) =>
          onChangeField(typePickup, {
            ...model[typePickup],
            serviceTime: dateFormat(time, 'HH:mm'),
          })
        }
        label={t('flight.service_time')}
        className="col-6 col-md-2"
        isSubmitted={isSubmitted}
        validation={getFlightInformationValidation(true, true)}
      />
      <FormGroupTimePicker
        value={new Date('2000-11-11T' + model[typePickup].pickupTime + 'Z')}
        onChange={(time) =>
          onChangeField(typePickup, { ...model[typePickup], pickupTime: dateFormat(time, 'HH:mm') })
        }
        label={t('flight.pickup_time')}
        className="col-6 col-md-2"
        isSubmitted={isSubmitted}
        validation={getFlightInformationValidation(true, true)}
      />
      <FormGroup
        value={model[type].transfer.dropOffDetails[dropoffType]?.desc}
        onChange={() => {}}
        label={
          model.forward.transfer.dropOffDetails.city ? t('hotel.address') : t('flight.airport')
        }
        className="col-12 col-md-6"
        disabled={true}
      />
      <InfoFormAddress
        isSubmitted={isSubmitted}
        type={dropoffType}
        onChange={(value) => onChangeField(typeDropoff, value)}
        value={model[typeDropoff]}
        validation={getFlightInformationValidation(pickupType === 'airport')}
      />
    </div>
  );
};

export default withErrorBoundary(InfoForm, 'InfoForm');
